import { Button, Dropdown, FormInput, RadioTabs } from '@basqet-dashboard/common/components';
import { COUNTRY_OPTIONS } from '@basqet-dashboard/common/config/countries';
import { InfoLabel } from '@components/onboarding';
import { ANALYTICS_EVENTS, KYB_ACCOUNT_TYPE } from '@config';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAnalytics, useTrackOnMount } from '@hooks';
import { useCompliance } from '@hooks/useCompliance';
import { mapFreelancerBasicDetails } from '@utils/compliance-mappers';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

import { ID_FORMAT_RULES } from '../../shared-constants';
import {
  ID_TYPE_OPTIONS,
  INDUSTRY_OPTIONS,
  MONTHLY_VOLUME_OPTIONS,
  PEP_RELATIONSHIP_OPTIONS,
  POSITION_STATUS_OPTIONS,
  SOURCE_OF_FUNDS_OPTIONS
} from '../freelancer-constants';
import { basicDetailsSchema, PEP_FIELDS } from './basic-details.schema';

const BasicDetails = ({ onComplete, initialData, readOnly = false }) => {
  const { track } = useAnalytics();
  const hasTrackedStart = useRef(false);

  useTrackOnMount(ANALYTICS_EVENTS.KYB_BASIC_INFO_VIEWED, {
    kyb_account_type: KYB_ACCOUNT_TYPE.FREELANCER
  });

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    clearErrors,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(basicDetailsSchema),
    defaultValues: { is_pep: 'no' }
  });

  useEffect(() => {
    if (initialData) {
      reset({
        brand_name: initialData.brand_name || '',
        industry: initialData.industry || '',
        custom_industry: initialData.custom_industry || '',
        nature_of_business: initialData.nature_of_business || '',
        business_address: initialData.business_address || '',
        country: initialData.country || '',
        bvn: initialData.bvn || '',
        id_type: initialData.id_type || '',
        id_number: initialData.id_number || '',
        is_pep: initialData.is_pep || 'no',
        pep_full_name: initialData.pep_full_name || '',
        pep_relationship: initialData.pep_relationship || '',
        pep_relationship_specify: initialData.pep_relationship_specify || '',
        pep_position: initialData.pep_position || '',
        pep_position_status: initialData.pep_position_status || '',
        pep_country: initialData.pep_country || '',
        monthly_volume: initialData.monthly_volume || '',
        source_of_funds: initialData.source_of_funds || ''
      });
    }
  }, [initialData, reset]);

  const watchedIndustry = useWatch({ control, name: 'industry' });
  const watchedIsPep = useWatch({ control, name: 'is_pep' });
  const watchedPepRelationship = useWatch({ control, name: 'pep_relationship' });
  const watchedIdType = useWatch({ control, name: 'id_type' });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const clearPepFields = () => {
    PEP_FIELDS.forEach((field) => setValue(field, '', { shouldValidate: false }));
    clearErrors(PEP_FIELDS);
  };

  const handleFormFocus = () => {
    if (!hasTrackedStart.current) {
      track(ANALYTICS_EVENTS.KYB_BASIC_INFO_STARTED, {
        kyb_account_type: KYB_ACCOUNT_TYPE.FREELANCER
      });
      hasTrackedStart.current = true;
    }
  };

  const { saveSection } = useCompliance();

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);

      track(ANALYTICS_EVENTS.KYB_BASIC_INFO_SAVED, {
        kyb_account_type: KYB_ACCOUNT_TYPE.FREELANCER
      });

      const apiData = mapFreelancerBasicDetails(data);
      const result = await saveSection('FREELANCER_BASIC_DETAILS', apiData, {
        entryId: initialData?._entryId,
        version: initialData?._version
      });

      onComplete({ ...data, _entryId: result.entry_id, _version: result.version });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save basic details:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="basic-details" onSubmit={handleSubmit(onSubmit)} onFocus={handleFormFocus}>
      <fieldset disabled={readOnly} style={{ border: 'none', padding: 0, margin: 0 }}>
        <div className="basic-details__section">
          <div className="basic-details__row">
            <div className="basic-details__field">
              <FormInput
                label={
                  <InfoLabel
                    text="Brand Name"
                    tooltip="This is the name you operate under and how clients or partners recognize you."
                  />
                }
                name="brand_name"
                placeholder="e.g. Jane's Designs"
                register={register('brand_name')}
                error={errors.brand_name?.message}
              />
            </div>

            <div className="basic-details__field">
              <Dropdown
                name="industry"
                formLabel="Select Industry"
                placeholder="Select industry"
                options={INDUSTRY_OPTIONS}
                register={register('industry')}
                defaultValue={initialData?.industry}
                error={errors.industry?.message}
              />
            </div>
          </div>

          {watchedIndustry === 'other' && (
            <div className="basic-details__field">
              <FormInput
                label="Please specify industry"
                name="custom_industry"
                placeholder="Enter your industry"
                register={register('custom_industry')}
                error={errors.custom_industry?.message}
              />
            </div>
          )}

          <div className="basic-details__field">
            <FormInput
              label="Nature of Business"
              name="nature_of_business"
              placeholder="e.g. Online retail of electronics"
              register={register('nature_of_business')}
              error={errors.nature_of_business?.message}
            />
          </div>

          <div className="basic-details__row">
            <div className="basic-details__field">
              <FormInput
                label="Business Address"
                name="business_address"
                placeholder="Enter your business address"
                register={register('business_address')}
                error={errors.business_address?.message}
              />
            </div>

            <div className="basic-details__field">
              <Dropdown
                name="country"
                formLabel="Which country are you in?"
                placeholder="Select country"
                options={COUNTRY_OPTIONS}
                register={register('country')}
                defaultValue={initialData?.country}
                error={errors.country?.message}
              />
            </div>
          </div>
        </div>

        <div className="basic-details__section">
          <div className="basic-details__field">
            <FormInput
              label="BVN"
              name="bvn"
              placeholder="Enter your 11-digit BVN"
              register={register('bvn')}
              error={errors.bvn?.message}
            />
          </div>

          <div className="basic-details__row">
            <div className="basic-details__field">
              <Dropdown
                name="id_type"
                formLabel="ID Type"
                placeholder="Select ID type"
                options={ID_TYPE_OPTIONS}
                register={register('id_type')}
                defaultValue={initialData?.id_type}
                error={errors.id_type?.message}
              />
            </div>

            <div className="basic-details__field">
              <FormInput
                label="ID Number"
                name="id_number"
                placeholder={ID_FORMAT_RULES[watchedIdType]?.placeholder || 'Enter your ID number'}
                register={register('id_number')}
                error={errors.id_number?.message}
              />
            </div>
          </div>
        </div>

        <div className="basic-details__section">
          <div className="basic-details__field">
            <Controller
              name="is_pep"
              control={control}
              render={({ field }) => (
                <RadioTabs
                  name="is_pep"
                  label={
                    <InfoLabel
                      text="Are you a Politically Exposed Person (PEP)?"
                      tooltip="Politically exposed persons are people who hold or have held public office, or are closely related to someone who does. Select Yes only if this applies."
                    />
                  }
                  options={[
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' }
                  ]}
                  value={field.value}
                  onChange={(val) => {
                    field.onChange(val);
                    if (val === 'no') clearPepFields();
                  }}
                  error={errors.is_pep?.message}
                />
              )}
            />
          </div>

          {watchedIsPep === 'yes' && (
            <div className="basic-details__pep-panel">
              <div className="basic-details__field">
                <FormInput
                  label="Full Name of PEP"
                  name="pep_full_name"
                  placeholder="Enter full name"
                  register={register('pep_full_name')}
                  error={errors.pep_full_name?.message}
                />
              </div>

              {/* Row 1: Relationship + (Specify if "others", otherwise Position) */}
              <div className="basic-details__row">
                <div className="basic-details__field">
                  <Dropdown
                    name="pep_relationship"
                    formLabel="Relationship to Govt. Official"
                    placeholder="Select an option"
                    options={PEP_RELATIONSHIP_OPTIONS}
                    register={register('pep_relationship')}
                    defaultValue={initialData?.pep_relationship}
                    error={errors.pep_relationship?.message}
                  />
                </div>
                <div className="basic-details__field">
                  {watchedPepRelationship === 'others' ? (
                    <FormInput
                      label="Specify Relationship"
                      name="pep_relationship_specify"
                      placeholder="Describe your relationship"
                      register={register('pep_relationship_specify')}
                      error={errors.pep_relationship_specify?.message}
                    />
                  ) : (
                    <FormInput
                      label="Position/Office Held"
                      name="pep_position"
                      placeholder="e.g Minister, Commissioner, Military officer"
                      register={register('pep_position')}
                      error={errors.pep_position?.message}
                    />
                  )}
                </div>
              </div>

              {/* Row 2: varies by "others" selection */}
              <div className="basic-details__row">
                <div className="basic-details__field">
                  {watchedPepRelationship === 'others' ? (
                    <FormInput
                      label="Position/Office Held"
                      name="pep_position"
                      placeholder="e.g Minister, Commissioner, Military officer"
                      register={register('pep_position')}
                      error={errors.pep_position?.message}
                    />
                  ) : (
                    <Dropdown
                      name="pep_position_status"
                      formLabel="Is this Position Current or Former?"
                      placeholder="Select an option"
                      options={POSITION_STATUS_OPTIONS}
                      register={register('pep_position_status')}
                      defaultValue={initialData?.pep_position_status}
                      error={errors.pep_position_status?.message}
                    />
                  )}
                </div>
                <div className="basic-details__field">
                  {watchedPepRelationship === 'others' ? (
                    <Dropdown
                      name="pep_position_status"
                      formLabel="Is this Position Current or Former?"
                      placeholder="Select an option"
                      options={POSITION_STATUS_OPTIONS}
                      register={register('pep_position_status')}
                      defaultValue={initialData?.pep_position_status}
                      error={errors.pep_position_status?.message}
                    />
                  ) : (
                    <Dropdown
                      name="pep_country"
                      formLabel="Country Where Position was Held"
                      placeholder="Select country"
                      options={COUNTRY_OPTIONS}
                      register={register('pep_country')}
                      defaultValue={initialData?.pep_country}
                      error={errors.pep_country?.message}
                    />
                  )}
                </div>
              </div>

              {/* Country full-width — only when "others" (otherwise already in row 2) */}
              {watchedPepRelationship === 'others' && (
                <div className="basic-details__field">
                  <Dropdown
                    name="pep_country"
                    formLabel="Country Where Position was Held"
                    placeholder="Select country"
                    options={COUNTRY_OPTIONS}
                    register={register('pep_country')}
                    defaultValue={initialData?.pep_country}
                    error={errors.pep_country?.message}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="basic-details__section">
          <div className="basic-details__row">
            <div className="basic-details__field">
              <Dropdown
                name="monthly_volume"
                formLabel="Monthly Transaction Volume"
                placeholder="Select range"
                options={MONTHLY_VOLUME_OPTIONS}
                register={register('monthly_volume')}
                defaultValue={initialData?.monthly_volume}
                error={errors.monthly_volume?.message}
              />
            </div>

            <div className="basic-details__field">
              <Dropdown
                name="source_of_funds"
                formLabel="Your Source of Funds"
                placeholder="Select source"
                options={SOURCE_OF_FUNDS_OPTIONS}
                register={register('source_of_funds')}
                defaultValue={initialData?.source_of_funds}
                error={errors.source_of_funds?.message}
              />
            </div>
          </div>
        </div>
      </fieldset>
      {!readOnly && (
        <Button type="submit" isFull isLoading={isSubmitting} disabled={!isValid}>
          Save and Continue
        </Button>
      )}
    </form>
  );
};

BasicDetails.propTypes = {
  onComplete: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  readOnly: PropTypes.bool
};

export { BasicDetails };
